import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {Disposers, PromiseState, watchPromise} from '../../../lib/helpers';
import {MediaStreamStateStore} from './parts/MediaStreamStateStore';
import {disposeMediaStream, getUserMedia, mediaStreamWithoutAudio, UserMediaState} from './models';

export class LocalVideoStore {
  @observable.ref
  userMedia: PromiseState<MediaStream> | null;

  private fromUserMediaId: string | null;
  @observable.ref
  userMediaWithoutAudio: MediaStream | null;

  @observable.ref
  mediaStreamState: MediaStreamStateStore | null;

  private readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.userMedia = null;
    this.fromUserMediaId = null;
    this.userMediaWithoutAudio = null;
    this.mediaStreamState = null;

    this.setup();
  }

  @computed
  get userMediaState(): UserMediaState {
    const userMedia = this.userMedia;
    if (userMedia === null) return null;
    if (userMedia.isPending) return 'pending';
    if (userMedia.isRejected) return 'rejected';
    const state = this.mediaStreamState?.state;
    if (state === null || state === undefined)
      return null;
    return state;
  }

  @action
  getUserMedia(refresh: boolean = false) {
    if (this.userMedia?.isPending === true)
      return;
    if (this.userMedia?.isResolved === true && !refresh)
      return;

    this.closeUserMedia();
    this.userMedia = watchPromise(getUserMedia());
  }

  @action
  closeUserMedia = () => {
    disposeMediaStream(this.userMedia?.value);
    this.mediaStreamState?.dispose();

    this.userMedia = null;
    this.userMediaWithoutAudio = null;
    this.mediaStreamState = null;
  };

  private setup() {
    this.disposers.add(this.closeUserMedia);
    this.disposers.add(reaction(
      () => this.userMedia?.value,
      this.syncUserMediaWithoutAudio
    ));
    this.disposers.add(reaction(
      () => this.userMedia?.value,
      this.syncUserMediaStateStore
    ));
  }

  @action
  private syncUserMediaWithoutAudio = () => {
    const media = this.userMedia?.value;

    if (media === null || media === undefined) {
      this.fromUserMediaId = null;
      this.userMediaWithoutAudio = null;
    }
    else if (media.id !== this.fromUserMediaId) {
      this.fromUserMediaId = media.id;
      this.userMediaWithoutAudio = mediaStreamWithoutAudio(media);
    }
  }

  @action
  private syncUserMediaStateStore = () => {
    const media = this.userMedia?.value;
    if (media === null || media === undefined) {
      this.mediaStreamState?.dispose();
      this.mediaStreamState = null;
    }
    else if (this.mediaStreamState === null)
      this.mediaStreamState = new MediaStreamStateStore(media);
    else if (media.id !== this.mediaStreamState.stream.id) {
      this.mediaStreamState.dispose();
      this.mediaStreamState = new MediaStreamStateStore(media);
    }
  }

  dispose = () => {
    this.disposers.dispose();
  };
}
